import { cn } from "@/lib/utils";
import type { Locale } from "@/i18n";

interface LocaleFlagProps {
  locale: Locale;
  className?: string;
}

/**
 * Inline SVG flags for the locale switcher.
 *
 * Why inline SVG instead of emoji or an asset bundle:
 *   - Deterministic across Windows / macOS / Linux (emoji flags render very
 *     differently per OS and font).
 *   - No network requests, no bundle bloat.
 *   - Sharper at small sizes than emoji.
 *
 * Sizing: viewBox is 20x14 (standard 5:3.5 flag ratio). Size controlled via
 * the surrounding wrapper's width/height. Both flags use a thin border via
 * `outline` so they read well on light surfaces.
 */
export function LocaleFlag({ locale, className }: LocaleFlagProps) {
  return (
    <span
      className={cn(
        "inline-block overflow-hidden rounded-[2px] ring-1 ring-foreground/10",
        "h-3 w-[18px] shrink-0",
        className,
      )}
      aria-hidden="true"
    >
      {locale === "sv" ? <FlagSv /> : <FlagEn />}
    </span>
  );
}

function FlagSv() {
  // Swedish flag — proportions simplified to fit a 20x14 viewBox.
  // Cross horizontal: y=5..7. Cross vertical: x=6..8.
  return (
    <svg
      viewBox="0 0 20 14"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
      className="block h-full w-full"
    >
      <rect width="20" height="14" fill="#006AA7" />
      <rect x="0" y="5" width="20" height="2" fill="#FECC00" />
      <rect x="6" y="0" width="2" height="14" fill="#FECC00" />
    </svg>
  );
}

function FlagEn() {
  // Union Jack — simplified stylized representation suitable for a tiny pill.
  // Order matters: blue base → white diagonal saltire → red diagonal saltire
  // (thinner) → white cross → red cross (thinner).
  return (
    <svg
      viewBox="0 0 20 14"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
      className="block h-full w-full"
    >
      <rect width="20" height="14" fill="#012169" />
      {/* White saltire (X) */}
      <path
        d="M0 0 L20 14 M20 0 L0 14"
        stroke="#FFFFFF"
        strokeWidth="2.4"
      />
      {/* Red saltire (X) overlaid, thinner */}
      <path
        d="M0 0 L20 14 M20 0 L0 14"
        stroke="#C8102E"
        strokeWidth="1"
      />
      {/* White cross */}
      <rect x="0" y="5" width="20" height="4" fill="#FFFFFF" />
      <rect x="8" y="0" width="4" height="14" fill="#FFFFFF" />
      {/* Red cross */}
      <rect x="0" y="6" width="20" height="2" fill="#C8102E" />
      <rect x="9" y="0" width="2" height="14" fill="#C8102E" />
    </svg>
  );
}
